"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { DesktopOrderControllerService } from "@/api";
import { useNotification } from "@/components/NotificationContext";
import { useAuth } from "@/components/login/AuthContext";
import { QRCodeSVG } from "qrcode.react";

type PointsOptionKey =
  | "TRIAL"
  | "ONE_YUAN"
  | "FIVE_YUAN"
  | "TEN_YUAN"
  | "FIFTY_YUAN"
  | "CUSTOM";
type PayType = "WECHAT" | "ALIPAY";
type ModalStep = "form" | "qrcode" | "timeout" | "success";

const pointsOptions: {
  key: PointsOptionKey;
  price: number;
  points: number;
  name: string;
}[] = [
  { key: "TRIAL", price: 0.01, points: 10, name: "体验额度" },
  { key: "ONE_YUAN", price: 1, points: 10, name: "1元10算力" },
  { key: "FIVE_YUAN", price: 5, points: 50, name: "5元50算力" },
  { key: "TEN_YUAN", price: 10, points: 100, name: "10元100算力" },
  { key: "FIFTY_YUAN", price: 50, points: 500, name: "50元500算力" },
  { key: "CUSTOM", price: 0, points: 0, name: "自定义金额" },
];

const optionKeyMap: Record<PointsOptionKey, string> = {
  TRIAL: "TRIAL",
  ONE_YUAN: "ONE_YUAN",
  FIVE_YUAN: "FIVE_YUAN",
  TEN_YUAN: "TEN_YUAN",
  FIFTY_YUAN: "FIFTY_YUAN",
  CUSTOM: "CUSTOM",
};

function fmtMoney(n: number): string {
  if (Number.isInteger(n)) return n.toString();
  return parseFloat(n.toFixed(2)).toString();
}

const COUNTDOWN_SECONDS = 300;

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function PointsContent() {
  const { showNotification } = useNotification();
  const { userData } = useAuth();
  const [selected, setSelected] = useState<PointsOptionKey>("TEN_YUAN");
  const [customMoney, setCustomMoney] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>("form");
  const [payType, setPayType] = useState<PayType>("WECHAT");
  const [contact, setContact] = useState("");
  const [renewAccount, setRenewAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [qrcodeUrl, setQrcodeUrl] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const [isResendOpen, setIsResendOpen] = useState(false);
  const [resendContract, setResendContract] = useState("");
  const [resendNewContract, setResendNewContract] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendErrors, setResendErrors] = useState<Record<string, string>>({});

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentOption = pointsOptions.find((o) => o.key === selected)!;
  const currentPrice =
    selected === "CUSTOM" ? parseFloat(customMoney) || 0 : currentOption.price;
  // 1元 = 10算力
  const customCredits = Math.round(currentPrice * 10);

  // 弹窗打开时锁定背景滚动，避免页面跳动
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  const clearTimers = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const closeModal = () => {
    clearTimers();
    setIsModalOpen(false);
    setModalStep("form");
  };

  const openResendModal = () => {
    setResendContract("");
    setResendNewContract("");
    setResendErrors({});
    setResendSuccess(false);
    setIsResendOpen(true);
  };

  const closeResendModal = () => {
    setIsResendOpen(false);
    setResendSuccess(false);
  };

  const handleResend = async () => {
    const newErrors: Record<string, string> = {};
    if (!resendContract.trim()) {
      newErrors.contract = "请输入联系方式（邮箱或手机）";
    }
    if (Object.keys(newErrors).length > 0) {
      setResendErrors(newErrors);
      return;
    }

    setResendLoading(true);
    const { data: result } =
      await DesktopOrderControllerService.resendRegisterCodeUsingPost({
        body: {
          contract: resendContract.trim(),
          newContract: resendNewContract.trim() || undefined,
        },
      });
    setResendLoading(false);

    if (result?.code !== "OK") {
      showNotification(result?.message || "重发注册码失败", { type: "error" });
      return;
    }
    setResendSuccess(true);
  };

  const openModal = () => {
    setIsModalOpen(true);
    setModalStep("form");
    setContact("");
    setRenewAccount("");
    setErrors({});
    setQrcodeUrl("");
    setOrderNo("");
    setCountdown(COUNTDOWN_SECONDS);
  };

  const handlePaid = useCallback(() => {
    clearTimers();
    setModalStep("success");
  }, [clearTimers]);

  useEffect(() => {
    if (modalStep !== "qrcode") return;

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          if (pollRef.current) clearInterval(pollRef.current);
          setModalStep("timeout");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [modalStep]);

  useEffect(() => {
    if (modalStep !== "qrcode" || !orderNo) return;

    const checkState = async () => {
      try {
        const { data: result } =
          await DesktopOrderControllerService.orderStateUsingGet({
            query: { orderNo },
          });
        const isPaid = result?.code === "OK" && result?.data === "SUCCESS";
        if (isPaid) {
          handlePaid();
        }
      } catch {
        // ignore poll errors
      }
    };

    checkState();
    pollRef.current = setInterval(checkState, 3000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [modalStep, orderNo, handlePaid]);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (selected === "CUSTOM" && (!customMoney || currentPrice <= 0)) {
      newErrors.customMoney = "请输入有效的自定义金额";
    }
    if (!userData && !contact.trim()) {
      newErrors.contact = "请输入联系方式（邮箱或手机）";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    const { data: result } =
      await DesktopOrderControllerService.createOrderUsingPost({
        body: {
          orderType: "POINTS",
          packageType: optionKeyMap[selected],
          customMoney: currentPrice,
          payType,
          remark: contact.trim(),
          renewAccount: renewAccount.trim() || undefined,
        },
      });

    if (result?.code !== "OK" || !result.data) {
      showNotification(result?.message || "创建订单失败", { type: "error" });
      setLoading(false);
      return;
    }

    const { qrcode, orderNo: returnedOrderNo } = result.data;
    setQrcodeUrl(qrcode || "");
    setOrderNo(returnedOrderNo || "");
    setCountdown(COUNTDOWN_SECONDS);
    setModalStep("qrcode");
    setLoading(false);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">购买算力</h1>
            <p className="text-xl opacity-90">充值算力，字幕提取轻松搞定</p>
            <p className="mt-4">
              <Link
                href="/sponsorship"
                className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-sm"
              >
                需要会员套餐？前往购买套餐 →
              </Link>
            </p>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-10">
          {/* 算力档位 */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10">
            {pointsOptions.map((opt) => {
              const active = selected === opt.key;
              const isCustom = opt.key === "CUSTOM";
              return (
                <div
                  key={opt.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(opt.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(opt.key);
                    }
                  }}
                  className={`relative flex flex-col items-center rounded-2xl p-6 transition-all cursor-pointer text-center outline-none ${
                    active
                      ? "bg-white border-2 border-[#3b82f6] shadow-lg"
                      : "bg-white border border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {opt.key === "TRIAL" && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white text-xs whitespace-nowrap">
                      体验额度
                    </span>
                  )}
                  <span className="text-gray-600">{opt.name}</span>
                  {isCustom ? (
                    active ? (
                      <div
                        className="mt-2 flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            type="text"
                            inputMode="numeric"
                            value={customMoney}
                            onChange={(e) => {
                              const v = e.target.value
                                .replace(/\D/g, "")
                                .replace(/^0+/, "");
                              setCustomMoney(v);
                              setSelected("CUSTOM");
                            }}
                            placeholder="输入金额"
                            className="w-24 py-1.5 text-center text-2xl font-extrabold text-gray-900 bg-transparent outline-none border-0 border-b-2 border-transparent focus:border-[#3b82f6] placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 transition-colors"
                          />
                          <span className="text-gray-500">元</span>
                        </div>
                        <span className="mt-1 text-sm text-[#3b82f6] font-medium min-h-[20px]">
                          {customCredits > 0
                            ? `${fmtMoney(customCredits)} 算力`
                            : ""}
                        </span>
                      </div>
                    ) : (
                      <span className="mt-2 text-2xl font-extrabold text-gray-900">
                        自定义
                      </span>
                    )
                  ) : (
                    <span className="mt-2 text-3xl font-extrabold text-gray-900">
                      {fmtMoney(opt.price)}
                      <span className="text-base font-medium text-gray-500">
                        {" "}
                        元
                      </span>
                    </span>
                  )}
                  {!isCustom && opt.points > 0 && (
                    <span className="mt-1 text-sm text-[#3b82f6] font-medium">
                      {opt.points} 算力
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={openModal}
            className="w-full py-3.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#3b82f6] to-[#2563eb] transition-all hover:opacity-90 cursor-pointer mb-10"
          >
            立即购买
          </button>

          <div className="bg-white rounded-2xl card-shadow p-6 leading-loose">
            <h2 className="font-bold text-lg mb-2">算力耗费说明</h2>
            <p className="pl-4">字幕提取：1分钟消耗 1 算力</p>
            <p className="pl-4 text-sm text-gray-400">
              不足 1 分钟按 1 分钟计算；支付成功后算力即时到账。
            </p>
          </div>

          <div className="bg-white rounded-2xl card-shadow p-6 leading-loose mt-6">
            <h2 className="font-bold text-lg mb-2">购买流程</h2>
            <p className="pl-4 text-red-500">
              注：如果没有收到验证码，请
              <Link href="/feedback" className="text-blue-500 hover:underline">
                联系我们
              </Link>
              或
              <button
                type="button"
                onClick={openResendModal}
                className="text-blue-500 hover:underline cursor-pointer"
              >
                点击重发
              </button>
              注册码
            </p>
            <p className="pl-4">
              1.选择合适的算力包（支持自定义金额），点击「立即购买」
            </p>
            <p className="pl-4">
              2.填写联系方式并确认支付
              {!userData && (
                <>
                  ，
                  <span className="text-red-500">
                    注意需要填写联系方式(邮箱或者手机)
                  </span>
                </>
              )}
            </p>
            <p className="pl-4">
              3.登录后充值，未填写充值账号则默认充值到当前登录账号；填写了登录账号则以
              <span className="text-red-500">填写的账号为准</span>
            </p>
            <p className="pl-4">4.我们将向备注的联系方式发送注册码。</p>
            <p className="pl-4">5.使用注册码，激活对应的算力</p>
          </div>
        </main>
      </div>

      {/* 重发注册码弹窗 */}
      {isResendOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeResendModal}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                重发注册码
              </h2>
              <button
                onClick={closeResendModal}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5">
              {resendSuccess ? (
                <div className="space-y-5 flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      注册码已重发
                    </h3>
                    <p className="text-gray-500 mt-1">
                      请注意查收，如仍未收到请
                      <Link
                        href="/feedback"
                        className="text-blue-500 hover:underline"
                      >
                        联系我们
                      </Link>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeResendModal}
                    className="px-6 py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 cursor-pointer"
                  >
                    确定
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      原联系方式
                    </label>
                    <input
                      type="text"
                      value={resendContract}
                      onChange={(e) => {
                        setResendContract(e.target.value);
                        setResendErrors({ ...resendErrors, contract: "" });
                      }}
                      placeholder="请输入下单时填写的邮箱或手机号"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                        resendErrors.contract
                          ? "border-red-500"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    />
                    {resendErrors.contract && (
                      <p className="text-xs text-red-500 mt-1">
                        {resendErrors.contract}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      新联系方式
                    </label>
                    <input
                      type="text"
                      value={resendNewContract}
                      onChange={(e) => setResendNewContract(e.target.value)}
                      placeholder="如需更换接收邮箱/手机请填写，选填"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendLoading}
                    className="w-full py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {resendLoading && (
                      <svg
                        className="w-5 h-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {resendLoading ? "提交中..." : "确认重发"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 下单弹窗 */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalStep === "form" && "确认订单"}
                {modalStep === "qrcode" && "扫码支付"}
                {(modalStep === "timeout" || modalStep === "success") &&
                  "支付结果"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5">
              {modalStep === "form" && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择算力包
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {pointsOptions.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setSelected(opt.key)}
                          className={`py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                            selected === opt.key
                              ? "border-blue-500 bg-blue-50 text-blue-600"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          {opt.key === "CUSTOM" ? (
                            "自定义"
                          ) : (
                            <>
                              <span className="block text-lg font-bold">
                                {fmtMoney(opt.price)}元
                              </span>
                              <span className="block text-xs">
                                {opt.points} 算力
                              </span>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {selected === "CUSTOM" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        自定义金额（元）
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={customMoney}
                        onChange={(e) => {
                          const v = e.target.value
                            .replace(/\D/g, "")
                            .replace(/^0+/, "");
                          setCustomMoney(v);
                          setErrors({ ...errors, customMoney: "" });
                        }}
                        placeholder="请输入充值金额"
                        className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                          errors.customMoney
                            ? "border-red-500"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      />
                      {errors.customMoney && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.customMoney}
                        </p>
                      )}
                      {customCredits > 0 && (
                        <p className="mt-2 text-sm text-[#3b82f6] font-medium">
                          可购买 {fmtMoney(customCredits)} 算力
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      支付方式
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPayType("WECHAT")}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                          payType === "WECHAT"
                            ? "border-green-500 bg-green-50 text-green-600"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        微信支付
                      </button>
                      <button
                        type="button"
                        onClick={() => setPayType("ALIPAY")}
                        className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                          payType === "ALIPAY"
                            ? "border-blue-500 bg-blue-50 text-blue-600"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        支付宝
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {userData ? "联系方式（选填）" : "联系方式"}
                    </label>
                    <input
                      type="text"
                      value={contact}
                      onChange={(e) => {
                        setContact(e.target.value);
                        setErrors({ ...errors, contact: "" });
                      }}
                      placeholder="请输入邮箱或手机号，用于下发注册码"
                      className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                        errors.contact
                          ? "border-red-500"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    />
                    {errors.contact && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.contact}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      充值账号（选填）
                    </label>
                    <input
                      type="text"
                      value={renewAccount}
                      onChange={(e) => setRenewAccount(e.target.value)}
                      placeholder="填写需要充值算力的登录账号"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">
                      应付金额（
                      {selected === "CUSTOM"
                        ? customCredits > 0
                          ? `自定义·${fmtMoney(customCredits)}算力`
                          : "自定义"
                        : `${fmtMoney(currentPrice)}元·${currentOption.points}算力`}
                      ）
                    </span>
                    <span className="text-2xl font-bold text-red-500">
                      {fmtMoney(currentPrice)}元
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loading && (
                      <svg
                        className="w-5 h-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {loading ? "创建订单中..." : "确认支付"}
                  </button>
                </div>
              )}

              {modalStep === "qrcode" && (
                <div className="space-y-5 flex flex-col items-center">
                  <div className="text-center">
                    <p className="text-gray-600 mb-1">
                      请使用
                      <span className="font-medium text-gray-900">
                        {payType === "WECHAT" ? "微信" : "支付宝"}
                      </span>
                      扫码支付
                    </p>
                    <p className="text-sm text-gray-500">
                      支付金额：
                      <span className="font-bold text-red-500">
                        {fmtMoney(currentPrice)}元
                      </span>
                    </p>
                  </div>

                  <div className="text-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-sm font-medium">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      剩余时间 {formatCountdown(countdown)}
                    </span>
                  </div>

                  {qrcodeUrl ? (
                    <div className="p-3 border border-gray-200 rounded-xl bg-white">
                      <QRCodeSVG
                        value={qrcodeUrl}
                        size={224}
                        level="M"
                        includeMargin
                      />
                    </div>
                  ) : (
                    <div className="w-56 h-56 flex items-center justify-center border border-gray-200 rounded-xl bg-gray-50 text-gray-400">
                      加载中...
                    </div>
                  )}

                  <p className="text-xs text-gray-400 text-center">
                    二维码有效期5分钟，过期后请重新下单
                  </p>
                </div>
              )}

              {modalStep === "timeout" && (
                <div className="space-y-5 flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      支付超时
                    </h3>
                    <p className="text-gray-500 mt-1">
                      支付二维码已过期，请重新下单支付
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setModalStep("form");
                      setCountdown(COUNTDOWN_SECONDS);
                    }}
                    className="px-6 py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 cursor-pointer"
                  >
                    重新下单
                  </button>
                </div>
              )}

              {modalStep === "success" && (
                <div className="space-y-5 flex flex-col items-center py-6">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      支付成功，注册码已发送
                    </h3>
                    <p className="text-gray-500 mt-1">
                      感谢你的支持，请注意查收注册码
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2.5 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white rounded-lg font-medium transition-all hover:opacity-90 cursor-pointer"
                  >
                    确定
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
